import authService from './authService';
import { formatISO } from 'date-fns';
import config from '../config';

const DATES_API_PREFIX = '/api/v1/dates';
const CALENDAR_API_PREFIX = '/api/v1/calendar'; // New prefix for the new endpoint

/**
 * NEW: Fetches enriched calendar events with references.
 * @param {Date} startDate - The start of the date range.
 * @param {Date} endDate - The end of the date range.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of CalendarEventResponse objects.
 */
export const getCalendarEvents = async (startDate, endDate) => {
  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required to fetch calendar events.");
  }
  const queryParams = new URLSearchParams({
    start: formatISO(startDate),
    end: formatISO(endDate),
  });
  const url = `${config.API_BASE_URL}${CALENDAR_API_PREFIX}?${queryParams.toString()}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch calendar events: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { /* ignore */ }
      throw new Error(errorDetail);
    }
    const events = await response.json();
    return events;
  } catch (error) {
    console.error('[calendarApi] Error fetching calendar events:', error);
    throw error;
  }
};

export const getDatesInRange = async (startDate, endDate, expandRrule = true) => {
  if (!startDate || !endDate) {
    throw new Error("Start and end dates are required to fetch dates.");
  }
  const queryParams = new URLSearchParams({
    start: formatISO(startDate),
    end: formatISO(endDate),
    expand_rrule: String(expandRrule),
  });
  const url = `${config.API_BASE_URL}${DATES_API_PREFIX}?${queryParams.toString()}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      let errorDetail = `Failed to fetch dates: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const dates = await response.json();
    return dates;
  } catch (error) {
    console.error('[calendarApi] Error fetching dates:', error);
    throw error;
  }
};

export const createDate = async (dateData) => {
  if (!dateData.title || !dateData.start_datetime || !dateData.end_datetime) {
    throw new Error("Title, start datetime, and end datetime are required to create a date.");
  }
  const payload = {
    title: dateData.title,
    start_datetime: formatISO(dateData.start_datetime),
    end_datetime: formatISO(dateData.end_datetime),
    description: dateData.description || null,
    all_day: dateData.all_day || false,
    is_public: false,
    metadata: dateData.metadata || {},
    rrule: dateData.rrule || null,
  };
  const url = `${config.API_BASE_URL}/api/v1/resources/date`;
  console.log(`[calendarApi] Creating date at: ${url} with payload:`, payload);
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to create date: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const newDateFromServer = await response.json();
    console.log(`[calendarApi] Created date successfully on server:`, newDateFromServer);
    const completeNewDateObject = {
      ...dateData,
      ...newDateFromServer,
      start_datetime: payload.start_datetime,
      end_datetime: payload.end_datetime,
      rrule: payload.rrule,
    };
    return completeNewDateObject;
  } catch (error) {
    console.error('[calendarApi] Error creating date:', error);
    throw error;
  }
};

export const updateDate = async (dateId, dateData) => {
  if (!dateId) throw new Error("Date ID is required for update.");
  const payload = { ...dateData };
  if (payload.start_datetime) {
    payload.start_datetime = formatISO(new Date(payload.start_datetime));
  }
  if (payload.end_datetime) {
    payload.end_datetime = formatISO(new Date(payload.end_datetime));
  }
  if ('rrule' in payload && payload.rrule === '') {
    payload.rrule = null;
  }
  const url = `${config.API_BASE_URL}/api/v1/resources/date/${dateId}`;
  console.log(`[calendarApi] Updating date (series) at: ${url} with payload:`, payload);
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update date: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const updatedDateFromServer = await response.json();
    console.log(`[calendarApi] Updated date successfully on server:`, updatedDateFromServer);
    const completeUpdatedDateObject = {
      ...dateData,
      ...updatedDateFromServer,
      start_datetime: payload.start_datetime,
      end_datetime: payload.end_datetime,
      rrule: payload.rrule,
    };
    return completeUpdatedDateObject;
  } catch (error) {
    console.error(`[calendarApi] Error updating date ${dateId}:`, error);
    throw error;
  }
};

export const updateDateInstance = async (seriesId, originalInstanceStart, newInstanceData) => {
    if (!seriesId || !originalInstanceStart || !newInstanceData) {
        throw new Error("Series ID, original start time, and new data are required to update an instance.");
    }
    const payload = {
        edit_scope: "instance",
        original_instance_start: formatISO(new Date(originalInstanceStart)),
        title: newInstanceData.title,
        description: newInstanceData.description,
        start_datetime: formatISO(newInstanceData.start_datetime),
        end_datetime: formatISO(newInstanceData.end_datetime),
        all_day: newInstanceData.all_day,
        metadata: newInstanceData.metadata,
    };
    const url = `${config.API_BASE_URL}/api/v1/resources/date/${seriesId}`;
    console.log(`[calendarApi] Updating single date instance at: ${url} with payload:`, payload);
    try {
        const response = await authService.authFetch(url, {
            method: 'PUT',
            body: payload,
        });
        if (!response.ok) {
            let errorDetail = `Failed to update date instance: ${response.status}`;
            try {
                const errorData = await response.json();
                errorDetail = errorData.detail || errorDetail;
            } catch (e) { }
            throw new Error(errorDetail);
        }
        const result = await response.json();
        console.log(`[calendarApi] Updated date instance successfully on server:`, result);
        return result;
    } catch (error) {
        console.error(`[calendarApi] Error updating date instance ${seriesId}:`, error);
        throw error;
    }
};

const calendarApi = {
  getCalendarEvents,
  getDatesInRange,
  createDate,
  updateDate,
  updateDateInstance,
};

export default calendarApi;